import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { colors, crewLabels, pick, ROOM_ICONS, type Crew, type RoomIcon } from '@hogar/shared';
import { Button, ErrorText, Field, Label, Screen, Segmented, confirm, useT } from '../components/ui';
import { hasGarden } from '../lib/crew';
import { BackHeader } from '../components/BackHeader';
import { roomIcons } from '../components/icons';
import { api } from '../lib/api';
import { useAction } from '../lib/session';
import { useHouse } from '../lib/house';

/** Add a room, or rename, change the icon of, or delete one. */
export default function RoomEdit() {
  const { id, crew } = useLocalSearchParams<{ id?: string; crew?: string }>();
  const { t, lang } = useT();
  const house = useHouse();
  const { rooms } = house;
  const act = useAction();
  const room = id ? rooms.find((r) => r.id === id) : undefined;
  const [name, setName] = useState(room ? pick(room.name, lang) : '');
  // Once there is a garden, an area can move between the house and the garden, with its tasks.
  const [roomCrew, setRoomCrew] = useState<Crew>(room?.crew ?? (crew === 'garden' ? 'garden' : 'house'));
  const [icon, setIcon] = useState<RoomIcon>(room?.icon ?? (crew === 'garden' ? 'flower-2' : 'house'));
  // English-speaking owners can correct the Spanish staff see.
  const [nameEs, setNameEs] = useState(room?.name.src === 'en' ? room.name.es : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => (router.canGoBack() ? router.back() : router.replace('/rutinas'));

  const save = async () => {
    setBusy(true);
    try {
      if (room) await act(() => api('PATCH', `/rooms/${room.id}`, { name: name.trim(), icon, crew: roomCrew, ...(room.name.src === 'en' && nameEs.trim() && nameEs.trim() !== room.name.es ? { nameEs: nameEs.trim() } : {}) }));
      else await act(() => api('POST', '/rooms', { name: name.trim(), icon, crew: roomCrew }));
      close();
    } catch {
      setError(t('errGeneric'));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!room || !(await confirm(t('confirmDeleteRoom'), t('delete'), t('cancel')))) return;
    await act(() => api('DELETE', `/rooms/${room.id}`));
    router.replace('/rutinas');
  };

  return (
    <Screen>
      <BackHeader title={room ? t('editRoom') : t('addRoom')} fallback="/rutinas" />
      <Label>{t('roomName')}</Label>
      <Field value={name} onChange={setName} autoFocus={!room} />
      {room && lang === 'en' && room.name.src === 'en' && (
        <>
          <Label>{t('spanishForStaff')}</Label>
          <Field value={nameEs} onChange={setNameEs} />
        </>
      )}
      {hasGarden(house) && (
        <>
          <Label>{t('belongsTo')}</Label>
          <Segmented<Crew> value={roomCrew} options={[{ value: 'house', label: crewLabels.house[lang] }, { value: 'garden', label: crewLabels.garden[lang] }]} onChange={setRoomCrew} />
        </>
      )}
      <Label>{t('icon')}</Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        {ROOM_ICONS.map((key) => {
          const Icon = roomIcons[key];
          const on = key === icon;
          return (
            <Pressable
              key={key}
              onPress={() => setIcon(key)}
              style={{ width: 56, height: 56, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? colors.primary : colors.card, borderWidth: 1, borderColor: on ? colors.primary : colors.cardBorder }}
            >
              <Icon size={24} color={on ? colors.white : colors.primary} strokeWidth={1.6} />
            </Pressable>
          );
        })}
      </View>
      <ErrorText>{error}</ErrorText>
      <Button label={t('save')} onPress={save} disabled={busy || !name.trim()} style={{ marginTop: 22 }} />
      {room && <Button label={t('deleteRoom')} variant="danger" onPress={remove} style={{ marginTop: 10 }} />}
    </Screen>
  );
}
